import React from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BG_COLORS } from '../../utils/colors';

const Footer: React.FC = () => {
  const { t } = useTranslation('navigation');
  // На узком экране подписи ссылок укорачиваются, иначе футер не укладывается в одну строку:
  // полные русские подписи вместе с копирайтом укладываются в строку только от ~725px
  // (на десктопных кеглях они занимают ~650px). Поэтому порог — планшетный 767px:
  // ниже него короткие подписи, с 768px полные, и футер везде остаётся в одну строку.
  const isNarrow = useMediaQuery('(max-width: 767px)', { noSsr: true });

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        width: '100%',
        gap: '10px',
        alignItems: 'flex-start',
        py: 3,
        px: 3,
        zIndex: 1000,
      }}
    >
      {/* Left section with copyright and legal links */}
      <Box
        sx={{
          display: 'flex',
          // center, а не baseline: подписи мельче копирайта, и по базовой линии они читаются
          // как «сидящие на той же строке». Оптически лучше, когда мелкий текст стоит по
          // центру крупного — низ букв при этом расходится, и это нормально.
          alignItems: 'center',
          gap: '15px',
          flexShrink: 1,
          flexGrow: 1,
          minWidth: 0,
          pl: 3,
          '@media (max-width: 579px)': {
            gap: '8px',
            pl: 1,
          },
          '@media (max-width: 439px)': {
            gap: '6px',
            pl: 1,
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontSize: '14px',
            // Межстрочный подобран так, чтобы САМИ БУКВЫ копирайта и подписей стояли по одному
            // центру. Боксы центрирует flex, но внутри бокса глифы сидят по-разному: у кнопки
            // (display:flex) текст ровно по центру, у копирайта — зависит от line-height.
            // При штатных 1.43 подписи visually уезжали вверх на ~2px, при 1 — вниз на ~1.5px.
            // Плато с расхождением ~0 приходится на 1.1–1.2, берём середину.
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '@media (max-width: 579px)': {
              fontSize: '11px',
            },
            '@media (max-width: 439px)': {
              fontSize: '10px',
            },
          }}
        >
          {t('footer.copyright')}
        </Typography>
        {/* Контейнер для кнопок - переносит на новую строку, когда не помещаются */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            rowGap: '2px',
            minWidth: 0,
            flexShrink: 1,
            flexGrow: 1,
            '@media (max-width: 579px)': {
              gap: '4px',
            },
            '@media (max-width: 439px)': {
              gap: '2px',
            },
          }}
        >
          <Button
            component={RouterLink}
            to="/terms"
            color="inherit"
            size="small"
            sx={{
              fontSize: '10px',
              // && — собственный класс MuiButton перебивает обычный sx по порядку в стилях.
              // Без этого у кнопки остаётся штатный line-height 1.75, её бокс становится выше,
              // и подписи перестают попадать в центр копирайта.
              '&&': { lineHeight: 1 },
              textTransform: 'uppercase',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                background: BG_COLORS.GLASS_LIGHT,
              },
              '@media (max-width: 579px)': {
                fontSize: '8px',
                padding: '2px 4px',
              },
              '@media (max-width: 439px)': {
                fontSize: '7px',
                padding: '2px 4px',
              },
            }}
          >
            {t(isNarrow ? 'footer.termsShort' : 'footer.terms')}
          </Button>
          <Button
            component={RouterLink}
            to="/privacy"
            color="inherit"
            size="small"
            sx={{
              fontSize: '10px',
              '&&': { lineHeight: 1 }, // см. комментарий у первой кнопки
              textTransform: 'uppercase',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                background: BG_COLORS.GLASS_LIGHT,
              },
              '@media (max-width: 579px)': {
                fontSize: '8px',
                padding: '2px 4px',
              },
              '@media (max-width: 439px)': {
                fontSize: '7px',
                padding: '2px 4px',
              },
            }}
          >
            {t(isNarrow ? 'footer.privacyShort' : 'footer.privacy')}
          </Button>
          <Button
            component={RouterLink}
            to="/rules"
            color="inherit"
            size="small"
            sx={{
              fontSize: '10px',
              '&&': { lineHeight: 1 }, // см. комментарий у первой кнопки
              textTransform: 'uppercase',
              minWidth: 'auto',
              padding: '4px 8px',
              '&:hover': {
                background: BG_COLORS.GLASS_LIGHT,
              },
              '@media (max-width: 579px)': {
                fontSize: '8px',
                padding: '2px 4px',
              },
              '@media (max-width: 439px)': {
                fontSize: '7px',
                padding: '2px 4px',
              },
            }}
          >
            {t(isNarrow ? 'footer.rulesShort' : 'footer.rules')}
          </Button>
        </Box>
      </Box>

    </Box>
  );
};

export default Footer;
